import React, { useState, useEffect } from 'react';
import { Search, Eye, Trash2, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { confirmAction } from '../../utils/toastUtils.jsx';
import styles from '../Admins/Admins.module.css';

const Businesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/superadmin/businesses');
      setBusinesses(data);
    } catch (error) {
      toast.error('Failed to fetch businesses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const handleStatusUpdate = async (id, newStatus) => {
    confirmAction(`Are you sure you want to mark this business as ${newStatus}?`, async () => {
      try {
        await api.put(`/superadmin/businesses/${id}/status`, { status: newStatus });
        toast.success(`Business ${newStatus.toLowerCase()} successfully!`);
        fetchBusinesses();
      } catch (error) {
        toast.error(`Failed to update status`);
      }
    });
  };

  const handleDelete = async (id) => {
    confirmAction('Are you sure you want to remove this business?', async () => {
      try {
        await api.delete(`/superadmin/businesses/${id}`);
        toast.success('Business removed successfully');
        fetchBusinesses();
      } catch (error) {
        console.error(error);
        toast.error(error.response?.data?.message || error.message || 'Failed to remove business');
      }
    });
  };

  const filteredBusinesses = businesses.filter(business => 
    business.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    business.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    business.ownerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.adminsContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.headerText}>
          <h2>Businesses</h2>
          <p>Manage all businesses operating within the societies</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.searchBar}>
            <Search size={18} className={styles.searchIcon} />
            <input 
              type="text" 
              placeholder="Search by name, owner or category..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading businesses...</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Category</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBusinesses.length > 0 ? (
                filteredBusinesses.map(business => (
                  <tr key={business._id}>
                    <td>
                      <div className={styles.adminInfo}>
                        <div className={styles.avatar}>
                          {business.name.charAt(0).toUpperCase()}
                        </div>
                        <span>{business.name}</span>
                      </div>
                    </td>
                    <td>{business.category}</td>
                    <td>{business.ownerName}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${business.status === 'Approved' ? styles.approved : ''}`} style={business.status !== 'Approved' ? { background: '#ffedd5', color: '#ea580c' } : {}}>
                        {business.status || 'Pending'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {business.status !== 'Approved' && (
                          <button 
                            onClick={() => handleStatusUpdate(business._id, 'Approved')}
                            style={{ color: '#16a34a', background: '#dcfce7', border: 'none', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            title="Approve Business"
                          >
                            <Check size={16} />
                          </button>
                        )}
                        {business.status !== 'Rejected' && (
                          <button 
                            onClick={() => handleStatusUpdate(business._id, 'Rejected')}
                            style={{ color: '#ea580c', background: '#ffedd5', border: 'none', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            title="Reject Business"
                          >
                            <X size={16} />
                          </button>
                        )}
                        <button className={styles.deleteBtn} onClick={() => handleDelete(business._id)} title="Delete Business">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className={styles.noData}>No businesses found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Businesses;
